import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { btnPrimary, btnSecondary, card, label } from "@/lib/styles";

const FEATURES_FREE = ["All 40 full lessons, free forever", "Key concepts, flashcards & quiz trials", "Placement & personalization", "3 graded interviews with Altaris (trial)"];
const FEATURES_PRO = ["Everything in Free", "Unlimited interviews with Altaris", "Longer audiences: 6 questions per interview vs 4", "Questions drawn from real Google, Meta, Amazon & top-lab interview loops", "New questions & topics added continuously, so prep never goes stale", "Full score history & weak-topic drills", "Altaris's sharpest, most demanding scrutiny"];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user || user === false) { navigate("/register"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan: billing, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const isPro = user?.plan === "pro";

  return (
    <div className="min-h-screen grid-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <span className={label}>pricing</span>
          <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Study free.<br /><span className="text-gold glow-gold italic">Go pro to get grilled.</span></h1>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="inline-flex border border-zinc-800 bg-zinc-950 p-1">
            <button data-testid="billing-monthly" onClick={() => setBilling("monthly")} className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${billing === "monthly" ? "bg-emerald-500 text-black" : "text-zinc-400"}`}>Monthly</button>
            <button data-testid="billing-annual" onClick={() => setBilling("annual")} className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${billing === "annual" ? "bg-emerald-500 text-black" : "text-zinc-400"}`}>Annual <span className="text-[9px]">(save 20%)</span></button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* FREE */}
          <div className={`${card} p-8`}>
            <h2 className="font-sans font-bold text-xl tracking-tight">Free</h2>
            <p className="mt-2"><span className="font-sans font-black text-4xl">$0</span></p>
            <ul className="mt-6 space-y-3">
              {FEATURES_FREE.map((f) => <li key={f} className="text-sm text-zinc-300 flex gap-2"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            <button onClick={() => navigate(user ? "/curriculum" : "/register")} className={`${btnSecondary} w-full mt-8`} data-testid="pricing-free-cta">{user ? "Go to curriculum" : "Start free"}</button>
          </div>

          {/* PRO */}
          <div className="border border-emerald-500/50 bg-emerald-950/10 p-8 scanlines relative shadow-[0_0_30px_rgba(217,164,65,0.18)]">
            <div className="flex items-center justify-between">
              <h2 className="font-sans font-bold text-xl tracking-tight flex items-center gap-2"><Crown size={18} className="text-yellow-400" /> Pro</h2>
              <span className="text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-500/50 px-2 py-1">unlimited</span>
            </div>
            <p className="mt-2">
              <span className="font-sans font-black text-4xl text-emerald-400">${billing === "monthly" ? "20" : "192"}</span>
              <span className="text-sm text-zinc-500">/{billing === "monthly" ? "mo" : "yr"}</span>
            </p>
            <ul className="mt-6 space-y-3">
              {FEATURES_PRO.map((f) => <li key={f} className="text-sm text-zinc-200 flex gap-2"><Zap size={16} className="text-emerald-400 shrink-0 mt-0.5" />{f}</li>)}
            </ul>
            {isPro ? (
              <button disabled className={`${btnPrimary} w-full mt-8`} data-testid="pricing-pro-active">✓ You're Pro</button>
            ) : (
              <button onClick={checkout} disabled={loading} className={`${btnPrimary} w-full mt-8`} data-testid="pricing-pro-cta">{loading ? "redirecting..." : "Upgrade to Pro"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
